import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { isApiError } from '../api/errors';
import { AuthProvider } from '../auth/AuthContext';
import { CACHE } from './query-config';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * The FALLBACK policy, not the policy. Every query states its own tier
         * from `CACHE` (see `query-config.ts`, which explains the tiers and why
         * money data uses `staleTime: 0`). These defaults only cover a query
         * that forgot to — so they are set to the conservative `record` tier
         * rather than to anything generous: forgetting to classify a query
         * should cost a little speed, never correctness.
         */
        staleTime: CACHE.record.staleTime,
        gcTime: CACHE.record.gcTime,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry an authorization failure. The api client already
          // refreshed and replayed once on a 401; retrying a 401 or a 403 just
          // hammers the endpoint and, on the rotation path, risks tripping the
          // server's reuse detector.
          if (isApiError(error) && (error.status === 401 || error.status === 403)) {
            return false;
          }
          return failureCount < 2;
        },
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  // Created in state, not at module scope: one client per app instance keeps
  // tests isolated and survives Fast Refresh without sharing a stale cache.
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* AuthProvider sits INSIDE the router: RequireAuth and LoginPage both
            need router hooks, and the provider's redirects are router-driven. */}
        <AuthProvider>{children}</AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
